import api from './api';

export const actionItemAPI = {
  // Get action items with filters
  getActionItems: async (filters = {}) => {
    try {
      const response = await api.get('/action-items', { params: filters });
      return {
        success: true,
        data: response.data.data || [],
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error fetching action items:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch action items',
        data: []
      };
    }
  },

  // Get action item statistics
  getActionItemStats: async () => {
    try {
      const response = await api.get('/action-items/stats');
      return {
        success: true,
        data: response.data.data || {}
      };
    } catch (error) {
      console.error('Error fetching stats:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch statistics',
        data: {}
      };
    }
  },

  // Get single action item
  getActionItem: async (id) => {
    try {
      const response = await api.get(`/action-items/${id}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching action item:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch action item'
      };
    }
  },

  // Create action item
  createActionItem: async (data) => {
    try {
      const response = await api.post('/action-items', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error creating action item:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create action item'
      };
    }
  },

  // Update action item
  updateActionItem: async (id, data) => {
    try {
      const response = await api.put(`/action-items/${id}`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error updating action item:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update action item'
      };
    }
  },

  // Update progress
  updateProgress: async (id, progress) => {
    try {
      const response = await api.patch(`/action-items/${id}/progress`, { progress });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error updating progress:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update progress'
      };
    }
  },

  // Update status
  updateStatus: async (id, status, notes = '') => {
    try {
      const response = await api.patch(`/action-items/${id}/status`, { status, notes });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error updating status:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update status'
      };
    }
  },

  // Submit completion for current user (assignee)
  submitForCompletion: async (taskId, formData) => {
    try {
      const response = await api.post(
        `/action-items/${taskId}/assignee/submit-completion`,
        formData,
        {
          headers: {
            'Content-Type': undefined
          }
        }
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error submitting completion:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to submit completion'
      };
    }
  },

  // ✅ NEW: Three-level approval system
  
  // Level 1: Immediate supervisor approves/rejects with grade
  approveL1: async (taskId, assigneeId, grade, qualityNotes, comments, decision = 'approve') => {
    try {
      const payload = decision === 'approve' 
        ? { decision: 'approve', grade, qualityNotes, comments }
        : { decision: 'reject', comments };

      const response = await api.post(
        `/action-items/${taskId}/assignee/${assigneeId}/approve-l1`,
        payload
      );
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error in L1 approval:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to process Level 1 approval'
      };
    }
  },

  // Level 2: Supervisor's supervisor reviews
  approveL2: async (taskId, assigneeId, decision, comments) => {
    try {
      const response = await api.post(
        `/action-items/${taskId}/assignee/${assigneeId}/approve-l2`,
        {
          decision, // 'approve' or 'reject'
          comments
        }
      );
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error in L2 approval:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to process Level 2 review'
      };
    }
  },

  // Level 3: Project creator gives final approval
  approveL3: async (taskId, assigneeId, decision, comments) => {
    try {
      const response = await api.post(
        `/action-items/${taskId}/assignee/${assigneeId}/approve-l3`,
        {
          decision, // 'approve' or 'reject'
          comments
        }
      );
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error in L3 approval:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to process Level 3 approval'
      };
    }
  },

  // Create personal task
  createPersonalTask: async (taskData) => {
    try {
      const response = await api.post('/action-items/personal', taskData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error creating personal task:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create personal task'
      };
    }
  },

  // Get milestone tasks
  getMilestoneTasks: async (milestoneId) => {
    try {
      const response = await api.get(`/action-items/milestone/${milestoneId}`);
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error fetching milestone tasks:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch milestone tasks',
        data: []
      };
    }
  },

  // Reassign task
  reassignTask: async (taskId, newAssignees) => {
    try {
      const response = await api.post(`/action-items/${taskId}/reassign`, {
        newAssignees
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error reassigning task:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to reassign task'
      };
    }
  },

  // Approve/Reject creation
  processCreationApproval: async (id, decision, comments = '') => {
    try {
      const response = await api.post(`/action-items/${id}/approve-creation`, {
        decision,
        comments
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error processing creation approval:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to process approval'
      };
    }
  },

  // Delete action item
  deleteActionItem: async (id) => {
    try {
      const response = await api.delete(`/action-items/${id}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error deleting action item:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete action item'
      };
    }
  },

  // Get project action items
  getProjectActionItems: async (projectId) => {
    try {
      const response = await api.get(`/action-items/project/${projectId}`);
      return {
        success: true,
        data: response.data.data || [],
        project: response.data.project
      };
    } catch (error) {
      console.error('Error fetching project action items:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch project action items',
        data: []
      };
    }
  }
};





